import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sun, Moon, Settings2, ChevronDown, ChevronRight, AlertTriangle, Table2 } from "lucide-react";
import {
  getParametrosEncargosMaoDeObra, putParametroEncargoMaoDeObra,
  putIncluirNoTotalMaoDeObra,
  getParametrosBeneficiosMaoDeObra, putParametroBeneficioMaoDeObra,
  getOrigemParametrosMaoDeObra, deleteParametrosEncargosMaoDeObra, deleteParametrosBeneficiosMaoDeObra,
  getTabelaSalarialMaoDeObra, putTabelaSalarialMaoDeObra, deleteTabelaSalarialMaoDeObra,
  getRoleFromToken,
} from "../../api";
import SideBarNavGestaoOrcamentaria from "../../componets/SideBarNavGestaoOrcamentaria";
import { MESES_ABREV } from "../../utils/orcamento";

const palette = (isLight) => ({
  bg: isLight ? "#F8F7FC" : "#0F0A1E",
  card: isLight ? "#FFFFFF" : "#1A1030",
  border: isLight ? "#E5E0F0" : "#2E2050",
  text: isLight ? "#1A1030" : "#EDE9FA",
  muted: isLight ? "#7C6FA0" : "#9585C0",
  brand: "#7C3AED",
  success: "#10B981",
  danger: "#EF4444",
});

const ANO_ATUAL = new Date().getFullYear();
const OPCOES_ANO = Array.from({ length: ANO_ATUAL + 2 - 2025 + 1 }, (_, i) => 2025 + i);
// PJ só tem o Recálculo Férias (% sobre a diferença salarial, no mês do reajuste).
const CODIGO_RECALCULO_FERIAS = "recalculo_ferias";
const REGIMES_TABS = [
  { value: "CLT", label: "CLT" },
  { value: "Estagio", label: "Estágio" },
  { value: "PJ", label: "PJ" },
];

const headerBtnStyle = {
  display: "flex", alignItems: "center", gap: 6,
  background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)",
  borderRadius: 8, padding: "7px 12px", cursor: "pointer", color: "#fff", fontSize: 12, fontWeight: 600,
};

// Premissas · Mão de Obra: extraído de MaoDeObraView.js (era um painel colapsável dentro de
// Custo/Despesas Mão de Obra) — aqui é a tela dedicada, sempre carregada aberta. Tudo aqui é
// global, igual para todos os departamentos (inclusive "Incluir no Total", que até 2026-09-23
// era por departamento) — por isso a tela não tem seletor de departamento, igual às demais
// Premissas. Valores por ano, herdados do último ano preenchido quando o ano não tem os seus.
export default function PremissasMaoDeObraView() {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const navigate = useNavigate();
  const isAdmin = getRoleFromToken() === "admin";

  const [ano, setAno] = useState(ANO_ATUAL+1);
  const [erro, setErro] = useState("");

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: C.bg, fontFamily: "'DM Sans', sans-serif", color: C.text }}>
      <SideBarNavGestaoOrcamentaria isLight={isLight} />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto" }}>
        <header style={{
          background: "linear-gradient(90deg, #4c1d95 0%, #9d174d 100%)", color: "#fff", padding: "14px 24px",
          display: "flex", justifyContent: "space-between", alignItems: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 22, fontWeight: 800 }}>YOUX</span>
            <span style={{ fontSize: 18, fontWeight: 300 }}>GROUP</span>
            <span style={{ fontSize: 13, fontWeight: 500, opacity: 0.8, marginLeft: 8 }}>· Premissas · Mão de Obra</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <select value={ano} onChange={(e) => setAno(parseInt(e.target.value, 10))}
              style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 10px", color: "#fff", fontSize: 13, fontWeight: 600 }}>
              {OPCOES_ANO.map((a) => <option key={a} value={a} style={{ color: "#0F172A" }}>{a}</option>)}
            </select>
            <button onClick={() => setIsLight((l) => !l)} style={headerBtnStyle}>
              {isLight ? <Sun size={14} /> : <Moon size={14} />} {isLight ? "Tema Claro" : "Tema Escuro"}
            </button>
            <button onClick={() => navigate("/inicio")} style={headerBtnStyle}>Menu Principal</button>
          </div>
        </header>

        <main style={{ padding: 24, display: "flex", flexDirection: "column", gap: 24 }}>
          {erro && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, background: `${C.danger}18`, border: `1px solid ${C.danger}`, color: C.danger, borderRadius: 10, padding: "10px 14px", fontSize: 13 }}>
              <AlertTriangle size={16} /> {erro}
            </div>
          )}

          <div>
            <h1 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: C.text }}>Premissas · Mão de Obra</h1>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: C.muted }}>
              Percentuais de Encargos, valores de Benefícios e Tabela Salarial usados no cálculo de custo de todo colaborador (Custo/Despesas Mão de Obra) — os mesmos para todos os departamentos. Ano sem valores próprios usa os do último ano preenchido.
            </p>
          </div>

          <PainelParametros C={C} ano={ano} onErro={setErro} isAdmin={isAdmin} />
          <PainelTabelaSalarial C={C} ano={ano} onErro={setErro} isAdmin={isAdmin} />
        </main>
      </div>
    </div>
  );
}

function PainelParametros({ C, ano, onErro, isAdmin }) {
  const [aba, setAba] = useState("CLT");
  const [encargos, setEncargos] = useState([]);
  const [beneficios, setBeneficios] = useState([]);
  // { encargos: { ano_origem, propria, pode_restaurar }, beneficios: {...} } — ano sem parâmetros
  // próprios herda os do último ano com parâmetros (mesma regra da Tabela Salarial).
  const [origem, setOrigem] = useState(null);
  const [carregando, setCarregando] = useState(false);

  async function carregar() {
    setCarregando(true);
    try {
      await recarregarSilencioso();
    } catch (e) {
      onErro(e.message);
    } finally {
      setCarregando(false);
    }
  }

  // Recarrega os dados sem exibir o indicador de carregamento — usada após editar uma
  // célula, para não desmontar os itens (o que fecharia as subcategorias já abertas).
  async function recarregarSilencioso() {
    const [e, b, o] = await Promise.all([
      getParametrosEncargosMaoDeObra(ano), getParametrosBeneficiosMaoDeObra(ano), getOrigemParametrosMaoDeObra(ano),
    ]);
    setEncargos(e || []);
    setBeneficios(b || []);
    setOrigem(o || null);
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { carregar(); }, [ano]);

  // Primeira edição num ano herdado: o backend copia o ano de origem inteiro para `ano` antes de
  // gravar — confirma antes, já que daí em diante o ano deixa de acompanhar o de origem.
  function confirmarEdicaoHerdada(tipo, rotulo) {
    const o = origem?.[tipo];
    if (!o || o.propria) return true;
    return window.confirm(
      `Os ${rotulo} de ${ano} hoje são herdados de ${o.ano_origem}. Ao editar, ${ano} passa a ter ${rotulo} próprios ` +
      `(cópia de ${o.ano_origem} + esta alteração) e os anos seguintes passam a herdar de ${ano}. Continuar?`
    );
  }

  async function executar(acao) {
    try {
      await acao();
    } catch (e) {
      onErro(e.message);
    }
    await recarregarSilencioso();
  }

  async function salvarCelulaEncargo(item, mes, percentual) {
    if (!confirmarEdicaoHerdada("encargos", "encargos")) return recarregarSilencioso();
    await executar(() => putParametroEncargoMaoDeObra(item.ids_mensais[mes], { percentual: percentual / 100 }, ano));
  }

  async function aplicarEncargo(item, meses, percentual) {
    if (!confirmarEdicaoHerdada("encargos", "encargos")) return;
    await executar(() => Promise.all(meses.map((mes) => putParametroEncargoMaoDeObra(item.ids_mensais[mes], { percentual: percentual / 100 }, ano))));
  }

  async function restaurar(tipo) {
    const rotulo = tipo === "encargos" ? "encargos" : "benefícios";
    if (!window.confirm(`Descartar os ${rotulo} próprios de ${ano}? O ano volta a herdar os do ano anterior.`)) return;
    await executar(() => (tipo === "encargos" ? deleteParametrosEncargosMaoDeObra(ano) : deleteParametrosBeneficiosMaoDeObra(ano)));
  }

  // "Incluir no Total" é global (todos os departamentos), vale nos 12 meses do ano e segue a
  // mesma herança entre anos que os percentuais.
  async function alternarIncluirNoTotalEncargo(item, incluirNoTotal) {
    if (!confirmarEdicaoHerdada("encargos", "encargos")) return;
    await executar(() => putIncluirNoTotalMaoDeObra(item.regime, item.codigo, ano, incluirNoTotal));
  }

  async function salvarCelulaBeneficio(item, mes, valor) {
    if (!confirmarEdicaoHerdada("beneficios", "benefícios")) return recarregarSilencioso();
    await executar(() => putParametroBeneficioMaoDeObra(item.ids_mensais[mes], { valor }, ano));
  }

  async function aplicarBeneficio(item, meses, valor) {
    if (!confirmarEdicaoHerdada("beneficios", "benefícios")) return;
    await executar(() => Promise.all(meses.map((mes) => putParametroBeneficioMaoDeObra(item.ids_mensais[mes], { valor }, ano))));
  }

  const encargosDaAba = encargos.filter((e) => e.regime === aba);
  const beneficiosDaAba = beneficios.filter((b) => b.regime === aba);

  return (
    <section style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 18px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Settings2 size={15} color={C.brand} />
          <h2 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: C.text }}>Parâmetros de Encargos e Benefícios</h2>
          <span style={{ fontSize: 10, fontWeight: 700, color: "#fff", background: C.brand, padding: "2px 8px", borderRadius: 20, textTransform: "uppercase", letterSpacing: "0.04em" }}>
            {isAdmin ? "Editável pelo financeiro" : "Somente visualização"}
          </span>
        </div>
      </div>

      <div style={{ padding: "0 18px 18px" }}>
        <p style={{ margin: "0 0 12px", fontSize: 11, color: C.muted }}>
          Nada aqui é fixo — os valores podem mudar mês a mês (reajuste de plano de saúde, mudança de alíquota, etc.).
        </p>
        <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
          {REGIMES_TABS.map((r) => (
            <button key={r.value} onClick={() => setAba(r.value)} style={{
              padding: "6px 14px", borderRadius: 8, border: "none", cursor: "pointer",
              background: aba === r.value ? C.brand : C.bg, color: aba === r.value ? "#fff" : C.text,
              fontSize: 12, fontWeight: 700, fontFamily: "inherit",
            }}>
              {r.label}
            </button>
          ))}
        </div>

        {carregando ? (
          <p style={{ fontSize: 12, color: C.muted }}>Carregando parâmetros…</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div>
              <CabecalhoOrigem C={C} titulo="Encargos (% sobre o salário do mês)" ano={ano} origem={origem?.encargos}
                isAdmin={isAdmin} onRestaurar={() => restaurar("encargos")} />
              {encargosDaAba.length === 0 ? (
                <p style={{ fontSize: 12, color: C.muted }}>Nenhum parâmetro cadastrado.</p>
              ) : encargosDaAba.map((item) => (
                <ItemParametroMensal key={`${item.regime}-${item.codigo}`} C={C} sufixo="%" fator={100}
                  item={item.codigo === CODIGO_RECALCULO_FERIAS
                    ? { ...item, label: `${item.label} (% sobre a diferença do reajuste, só no mês do reajuste)` }
                    : item}
                  onSalvarCelula={salvarCelulaEncargo} onAplicar={aplicarEncargo}
                  onToggleIncluirNoTotal={alternarIncluirNoTotalEncargo} isAdmin={isAdmin} />
              ))}
            </div>
            <div>
              <CabecalhoOrigem C={C} titulo="Benefícios (R$/mês)" ano={ano} origem={origem?.beneficios}
                isAdmin={isAdmin} onRestaurar={() => restaurar("beneficios")} />
              {beneficiosDaAba.length === 0 ? (
                <p style={{ fontSize: 12, color: C.muted }}>Nenhum parâmetro cadastrado.</p>
              ) : beneficiosDaAba.map((item) => (
                <ItemParametroMensal key={`${item.regime}-${item.codigo}`} C={C}
                  item={{ ...item, label: `${item.label}${item.apenas_lider ? " (Líder)" : item.apenas_colaborador ? " (Colaborador)" : ""}${item.exclui_remoto ? " (não aplica a Remoto)" : ""}` }}
                  prefixo="R$" fator={1} onSalvarCelula={salvarCelulaBeneficio} onAplicar={aplicarBeneficio} isAdmin={isAdmin} />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

// ── Tabela Salarial (Agrupamento × Enquadramento) ─────────────────────────────
// Uma por ano: o ano sem tabela própria herda a do último ano cadastrado, corrigida pelo índice
// de reajuste (Premissas Globais). Só o admin edita — a primeira edição de um ano herdado grava
// a grade inteira como própria daquele ano (os anos seguintes passam a herdar dela), e
// "Voltar a herdar" descarta a tabela própria. A base (primeiro ano cadastrado) não pode ser
// descartada. Colaboradores sem salário de exceção leem o salário desta tabela mês a mês.

const formatarMoeda = (v) => Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function PainelTabelaSalarial({ C, ano, onErro, isAdmin }) {
  const [tabela, setTabela] = useState(null);
  const [aberto, setAberto] = useState(true);

  useEffect(() => {
    let cancelado = false;
    setTabela(null);
    getTabelaSalarialMaoDeObra(ano)
      .then((t) => { if (!cancelado) setTabela(t); })
      .catch((e) => { if (!cancelado) onErro(e.message); });
    return () => { cancelado = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ano]);

  async function salvarCelula(agrupamento, enquadramento, valor) {
    if (!tabela.propria && !window.confirm(
      `A tabela de ${ano} hoje é herdada de ${tabela.ano_origem}. Ao editar, ela passa a ser própria de ${ano} ` +
      `(com os valores exibidos + esta alteração) e o índice de reajuste de ${ano} deixa de corrigi-la. Continuar?`
    )) return;
    try {
      setTabela(await putTabelaSalarialMaoDeObra(ano, { agrupamento, enquadramento, valor_referencia: valor }));
    } catch (e) {
      onErro(e.message);
    }
  }

  async function restaurar() {
    if (!window.confirm(`Descartar a tabela própria de ${ano}? Ela volta a herdar a do ano anterior (corrigida pelo índice de reajuste).`)) return;
    try {
      setTabela(await deleteTabelaSalarialMaoDeObra(ano));
    } catch (e) {
      onErro(e.message);
    }
  }

  const agrupamentos = tabela ? [...new Set(tabela.itens.map((i) => i.agrupamento))].sort((a, b) => a - b) : [];
  const enquadramentos = tabela ? [...new Set(tabela.itens.map((i) => i.enquadramento))].sort((a, b) => a - b) : [];
  const valorDe = (agr, enq) => tabela.itens.find((i) => i.agrupamento === agr && i.enquadramento === enq)?.valor_referencia;
  const origem = !tabela ? "" : tabela.propria
    ? `Própria de ${ano}`
    : `Herdada de ${tabela.ano_origem}${ano > tabela.ano_origem ? " + índice de reajuste" : ""}`;

  return (
    <section style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
      <div onClick={() => setAberto((v) => !v)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 18px", cursor: "pointer", gap: 8, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {aberto ? <ChevronDown size={14} color={C.muted} /> : <ChevronRight size={14} color={C.muted} />}
          <Table2 size={15} color={C.brand} />
          <h2 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: C.text }}>Tabela Salarial {ano}</h2>
          {tabela && (
            <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20, letterSpacing: "0.04em",
              color: tabela.propria ? "#fff" : C.muted, background: tabela.propria ? C.success : C.bg, border: tabela.propria ? "none" : `1px solid ${C.border}` }}>
              {origem}
            </span>
          )}
          <span style={{ fontSize: 10, fontWeight: 700, color: "#fff", background: C.brand, padding: "2px 8px", borderRadius: 20, textTransform: "uppercase", letterSpacing: "0.04em" }}>
            {isAdmin ? "Editável pelo admin" : "Somente visualização"}
          </span>
        </div>
        {isAdmin && tabela?.pode_restaurar && (
          <button onClick={(e) => { e.stopPropagation(); restaurar(); }} style={miniBtnStyle(C, false)}>Voltar a herdar do ano anterior</button>
        )}
      </div>

      {aberto && (
        <div style={{ padding: "0 18px 18px" }}>
          <p style={{ margin: "0 0 12px", fontSize: 11, color: C.muted }}>
            Salário base (R$/mês, valor de janeiro) por Agrupamento × Enquadramento — usado por todo colaborador sem salário de exceção.
            {tabela && !tabela.propria && ` Sem tabela própria em ${ano}: os valores vêm de ${tabela.ano_origem}${ano > tabela.ano_origem ? ", corrigidos pelo índice de reajuste" : ""}.`}
          </p>
          {!tabela ? (
            <p style={{ fontSize: 12, color: C.muted }}>Carregando tabela…</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ borderCollapse: "collapse", fontSize: 11, width: "100%" }}>
                <thead>
                  <tr>
                    <th style={{ padding: "6px 8px", textAlign: "left", color: C.muted, fontWeight: 700, whiteSpace: "nowrap" }}>Agrup. / Enq.</th>
                    {enquadramentos.map((enq) => (
                      <th key={enq} style={{ padding: "6px 4px", textAlign: "right", color: C.muted, fontWeight: 700 }}>{Math.round(enq * 100)}%</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {agrupamentos.map((agr) => (
                    <tr key={agr} style={{ borderTop: `1px solid ${C.border}` }}>
                      <td style={{ padding: "4px 8px", fontWeight: 700, color: C.text }}>{agr}</td>
                      {enquadramentos.map((enq) => (
                        <td key={enq} style={{ padding: "3px 2px" }}>
                          <CelulaTabelaSalarial C={C} valor={valorDe(agr, enq)} disabled={!isAdmin}
                            onSalvar={(v) => salvarCelula(agr, enq, v)} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function CelulaTabelaSalarial({ C, valor, onSalvar, disabled }) {
  const [texto, setTexto] = useState("");
  const [editando, setEditando] = useState(false);

  function confirmar() {
    setEditando(false);
    const normalizado = String(texto).trim().replace(/\./g, "").replace(",", ".");
    const novo = parseFloat(normalizado);
    if (!Number.isNaN(novo) && novo >= 0 && Math.abs(novo - Number(valor)) > 0.004) onSalvar(novo);
  }

  return (
    <input type="text" inputMode="decimal" disabled={disabled}
      value={editando ? texto : formatarMoeda(valor)}
      onFocus={() => { setEditando(true); setTexto(formatarMoeda(valor)); }}
      onChange={(e) => setTexto(e.target.value)} onBlur={confirmar}
      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") { setEditando(false); } }}
      style={{ width: "100%", minWidth: 78, boxSizing: "border-box", textAlign: "right", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: "4px 6px", fontSize: 11, color: C.text, fontFamily: "inherit", outline: "none", opacity: disabled ? 0.7 : 1 }} />
  );
}

// Título da seção + de onde vêm os valores do ano ("Próprios de 2027" / "Herdados de 2026") e,
// para o admin, "Voltar a herdar" quando o ano tem valores próprios e existe ano anterior.
function CabecalhoOrigem({ C, titulo, ano, origem, isAdmin, onRestaurar }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", margin: "0 0 6px" }}>
      <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>{titulo}</p>
      {origem && (
        <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
          color: origem.propria ? "#fff" : C.muted, background: origem.propria ? C.success : C.bg, border: origem.propria ? "none" : `1px solid ${C.border}` }}
          title={origem.propria ? undefined : `Nada cadastrado em ${ano} — valores de ${origem.ano_origem}. Editar qualquer célula cria os valores próprios de ${ano}.`}>
          {origem.propria ? `Próprios de ${ano}` : `Herdados de ${origem.ano_origem}`}
        </span>
      )}
      {isAdmin && origem?.pode_restaurar && (
        <button onClick={onRestaurar} style={miniBtnStyle(C, false)}>Voltar a herdar do ano anterior</button>
      )}
    </div>
  );
}

function formatarValor(valor, prefixo, sufixo) {
  if (prefixo) return `${prefixo} ${valor}`;
  return `${valor}${sufixo || ""}`;
}

// Item com 12 células mensais (Jan–Dez) + ações "Aplicar a todos os meses"/"Aplicar deste
// mês em diante", que tomam como base o último valor editado nesse item.
function ItemParametroMensal({ C, item, prefixo, sufixo, fator, onSalvarCelula, onAplicar, onToggleIncluirNoTotal, isAdmin }) {
  const [aberto, setAberto] = useState(false);
  const [ultimaEdicao, setUltimaEdicao] = useState(null); // { mes, valor }

  function registrarEdicao(mes, valor) {
    if (!isAdmin) return;
    setUltimaEdicao({ mes, valor });
    onSalvarCelula(item, mes, valor);
  }

  function aplicarTodos() {
    if (!ultimaEdicao) return;
    const meses = Array.from({ length: 12 }, (_, i) => i + 1);
    if (!window.confirm(`Aplicar "${formatarValor(ultimaEdicao.valor, prefixo, sufixo)}" (${MESES_ABREV[ultimaEdicao.mes - 1]}) para todos os 12 meses de "${item.label}"? Isso sobrescreve os outros meses.`)) return;
    onAplicar(item, meses, ultimaEdicao.valor);
  }

  function aplicarDesteMesEmDiante() {
    if (!ultimaEdicao) return;
    const meses = Array.from({ length: 12 }, (_, i) => i + 1).filter((m) => m >= ultimaEdicao.mes);
    if (!window.confirm(`Aplicar "${formatarValor(ultimaEdicao.valor, prefixo, sufixo)}" de ${MESES_ABREV[ultimaEdicao.mes - 1]} em diante para "${item.label}"?`)) return;
    onAplicar(item, meses, ultimaEdicao.valor);
  }

  const resumo = resumoItem(item, prefixo, sufixo, fator);

  return (
    <div style={{ padding: "6px 0", borderBottom: `1px solid ${C.border}` }}>
      <div
        onClick={() => setAberto((v) => !v)}
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, cursor: "pointer", padding: "2px 0" }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, minWidth: 0 }}>
          {aberto ? <ChevronDown size={13} color={C.muted} style={{ flexShrink: 0 }} /> : <ChevronRight size={13} color={C.muted} style={{ flexShrink: 0 }} />}
          <span style={{ color: C.text, fontWeight: 600 }}>{item.label}</span>
          <span style={{ color: C.muted }}>— {resumo}</span>
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }} onClick={(e) => e.stopPropagation()}>
          {onToggleIncluirNoTotal && (
            <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: C.muted, cursor: isAdmin ? "pointer" : "default" }}
              title="Se desmarcado, o item continua calculado e aparece na composição do custo, mas não soma no Total de Encargos/custo total">
              <input type="checkbox" checked={!!item.incluir_no_total} disabled={!isAdmin}
                onChange={(e) => onToggleIncluirNoTotal(item, e.target.checked)}
                style={{ cursor: isAdmin ? "pointer" : "default" }} />
              Incluir no Total
            </label>
          )}
          {aberto && isAdmin && (
            <div style={{ display: "flex", gap: 6 }}>
              <button onClick={aplicarTodos} disabled={!ultimaEdicao} style={miniBtnStyle(C, !ultimaEdicao)}>Aplicar a todos os meses</button>
              <button onClick={aplicarDesteMesEmDiante} disabled={!ultimaEdicao} style={miniBtnStyle(C, !ultimaEdicao)}>Aplicar deste mês em diante</button>
            </div>
          )}
        </div>
      </div>
      {aberto && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(12, minmax(52px, 1fr))", gap: 4, overflowX: "auto", marginTop: 8 }}>
          {MESES_ABREV.map((m, i) => {
            const mes = i + 1;
            return (
              <CelulaParametroMes key={mes} C={C} mesLabel={m} valor={Number(item.valores_mensais[mes] || 0) * fator}
                prefixo={prefixo} sufixo={sufixo} onSalvar={(v) => registrarEdicao(mes, v)} disabled={!isAdmin} />
            );
          })}
        </div>
      )}
    </div>
  );
}

// Resumo do item fechado — mesmo valor nos 12 meses, uma única mudança no ano (com o mês em
// que ela começa), ou "varia ao longo do ano" quando há mais de uma mudança.
function resumoItem(item, prefixo, sufixo, fator) {
  const arredondar = (v) => Math.round(v * 10000) / 10000;
  const valores = Array.from({ length: 12 }, (_, i) => arredondar(Number(item.valores_mensais[i + 1] || 0) * fator));

  if (valores.every((v) => v === valores[0])) {
    return `${formatarValor(valores[0], prefixo, sufixo)} (todos os meses)`;
  }

  const mudancas = [];
  for (let i = 1; i < 12; i++) {
    if (valores[i] !== valores[i - 1]) mudancas.push(i);
  }
  if (mudancas.length === 1) {
    const idx = mudancas[0];
    return `varia (${formatarValor(valores[idx - 1], prefixo, sufixo)} → ${formatarValor(valores[idx], prefixo, sufixo)} a partir de ${MESES_ABREV[idx]})`;
  }
  return "varia ao longo do ano";
}

function CelulaParametroMes({ C, mesLabel, valor, prefixo, sufixo, onSalvar, disabled }) {
  const [texto, setTexto] = useState(String(valor));
  const [editando, setEditando] = useState(false);

  useEffect(() => { if (!editando) setTexto(String(valor)); }, [valor, editando]);

  function confirmar() {
    setEditando(false);
    const novo = parseFloat(String(texto).replace(",", ".")) || 0;
    if (novo !== Number(valor)) onSalvar(novo);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "center" }}>
      <span style={{ fontSize: 9, color: C.muted, fontWeight: 600 }}>{mesLabel}</span>
      <span style={{ display: "flex", alignItems: "center", gap: 2 }}>
        {prefixo && <span style={{ fontSize: 9, color: C.muted }}>{prefixo}</span>}
        <input type="text" inputMode="decimal" value={editando ? texto : String(valor)} disabled={disabled}
          onFocus={() => { setEditando(true); setTexto(String(valor)); }}
          onChange={(e) => setTexto(e.target.value)} onBlur={confirmar}
          onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
          style={{ width: 48, textAlign: "right", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: "4px 4px", fontSize: 11, color: C.text, fontFamily: "inherit", outline: "none", opacity: disabled ? 0.6 : 1 }} />
        {sufixo && <span style={{ fontSize: 9, color: C.muted }}>{sufixo}</span>}
      </span>
    </div>
  );
}

function miniBtnStyle(C, disabled) {
  return {
    background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6,
    padding: "4px 8px", fontSize: 10, fontWeight: 600, color: disabled ? C.muted : C.brand,
    cursor: disabled ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: disabled ? 0.5 : 1,
  };
}
